import React, { useMemo, useState } from 'react'
import { Modal } from '../Modal/Modal'
import { SimpleConsentIcon } from './ConsentIcon'
import { ExternalUserConsentChange, User } from '../../models/User'
import { Container } from 'react-bootstrap'
import './ConsentModal.scss'
import { UserConsent, UserConsentState } from './model'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { parseMetadata } from '../../apps/professional-app/fa/MeetingSummary/utils'
import { TableColumn } from 'react-data-table-component'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { NavLink } from 'react-router-dom'
import buttonStyles from '../Button/Button.module.scss'
import { useGetExternalUserConsentHistoryQuery, useUpdateExternalUserConsentMutation } from '../../store/externalUser/externalUser'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useLazyDownloadGlobalFileRequestQuery } from '../../store/media/file'
import { useConsentManualConfigs } from './useConsentManualConfigs'
import { ZocksDataTable } from '../DataTable/ZocksDataTable'
import { ConsentAvatar } from './ConsentAvatar'
import { SERVICE_TOKEN_USER_ID } from '../../common/constants'
import { ConsentStateDropdown } from './ConsentStateDropdown'
import { useGetAllUsersQuery } from '../../store/user/user'
import { ConsentConfirmationPanel } from './ConsentConfirmationPanel'
import { useZocksAuth, useZocksQuota } from '../ScopedBlock/hooks'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useMeetingTitles } from './useMeetingTitles'
import { Button } from '../Button/Button'
import { useUserTimezone } from '../../common/useUserTimezone'
type ConsentModalProps = {
  show: boolean
  onCancel: () => void
  contact: User
  consent?: UserConsent | null
  roomId?: string
  sessionId?: string
}

const consentTypeLabel: Record<string, string> = {
  initial: 'Automatic',
  automatic: 'Automatic',
  manual: 'Manual',
}

const CommentExpandableRow: React.FC<{ data: ExternalUserConsentChange; auditLogEnabled?: boolean }> = ({ data, auditLogEnabled }) => {
  const parseReason = (reason: string): string[] => {
    const trimmed = reason.trim()
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean)
      } catch {
        const inner = trimmed.slice(1, -1)
        return inner
          .split(',')
          .map((s) => s.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean)
      }
    }
    return [reason]
  }

  const isInitial = data.type === 'initial'
  const hasReason = !!data.reason
  const isDeleted = !!data.deletedAt

  if (!isInitial && !isDeleted && !hasReason) return null

  const reasons = hasReason ? parseReason(data.reason!) : []

  return (
    <div className='zocks-bg-color-gray-100 border-bottom d-flex flex-column'>
      {isDeleted && (
        <div className='d-flex align-items-center gap-2 px-3 py-3 paragraph-medium'>
          <span className='zocks-text-gray-700'>System note:</span>
          <span className='zocks-text-gray-900'>
            This client was removed from the meeting. Their consent record was cleared automatically.
            {auditLogEnabled && (
              <NavLink to='/analytics-hub' target='_blank' rel='noopener noreferrer' className='zocks-text-primary text-decoration-underline'>
                See audit logs for details
              </NavLink>
            )}
          </span>
        </div>
      )}
      {hasReason && (
        <div className='px-4 py-3 paragraph-medium zocks-text-gray-900 d-flex flex-column'>
          {reasons.map((r, i) => {
            const colonIndex = r.indexOf(':')
            if (colonIndex === -1) return <span key={i}>{r}</span>
            const name = r.substring(0, colonIndex + 1)
            const text = r.substring(colonIndex + 1).trim()
            return (
              <div key={i}>
                <span>{name} </span>
                {text && <span>"{text}"</span>}
              </div>
            )
          })}
        </div>
      )}
      {isInitial && (
        <div className='d-flex align-items-center gap-2 px-4 py-3 paragraph-medium'>
          <span className='zocks-text-gray-700'>System note:</span>
          <span className='zocks-text-gray-900'>
            This entry was recorded before consent history tracking was introduced. The date and the person who set it aren't available.
          </span>
        </div>
      )}
    </div>
  )
}

const DEFAULT_PAGE_SIZE = 5

export const ConsentModal: React.FC<ConsentModalProps> = ({ consent, show, onCancel, contact, roomId, sessionId }) => {
  const accountId = useAppSelector(selectAccountId)
  const metadata = parseMetadata(contact.metadata ?? '')
  const userConsent = consent ?? (typeof metadata === 'object' && metadata !== null && metadata.zocksConsent)
  const consentState = userConsent?.current?.state ?? UserConsentState.UNKNOWN
  const [selectedConsentState, setSelectedConsentState] = useState<UserConsentState>(consentState)
  const [pendingConsentState, setPendingConsentState] = useState<UserConsentState | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [isSaving, setIsSaving] = useState(false)
  const { hasScope } = useZocksAuth()
  const { tracked } = useTracking()

  const [updateConsent] = useUpdateExternalUserConsentMutation()
  const [downloadFileRequest] = useLazyDownloadGlobalFileRequestQuery()
  const hasManualConsentManagementAdminScope = hasScope('**', 'consent_management:admin', [])
  const { hasQuota } = useZocksQuota()
  const manualConsentTrackingAvailable = hasQuota('identity.manualConsentTrackingEnabled.value')
  const { evidenceUpload: evidenceUploadConfig, commentUpload: commentUploadConfig, userConsentManualOverride } = useConsentManualConfigs()
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const meetingLevelConsent = account?.data.effectiveConfig.complianceConfig.userConsentManualConfigs.meetingLevelConsent ?? false
  const auditLogEnabled = account?.data.effectiveConfig.auditLogConfigs?.enabled ?? false
  const manualConsentEnabled = ((userConsentManualOverride || hasManualConsentManagementAdminScope) && manualConsentTrackingAvailable) ?? false

  const requiresConfirmationPanel = manualConsentEnabled && ((evidenceUploadConfig?.enabled || commentUploadConfig?.enabled) ?? false)

  const timezone = useUserTimezone()

  const filterByMeeting = meetingLevelConsent && !!roomId && !!sessionId

  const { data: consentChanges, isFetching: isConsentChangesFetching } = useGetExternalUserConsentHistoryQuery(
    {
      accountId,
      userId: contact.id,
      pageRequest: { page: page - 1, size: perPage },
      ...(filterByMeeting ? { roomId, sessionId } : {}),
    },
    { refetchOnMountOrArgChange: true },
  )

  const changedByUserIds = useMemo(() => [...new Set((consentChanges?.data ?? []).map((c) => c.changedByUserId).filter(Boolean))], [consentChanges?.data])

  const { data: changedByUsers } = useGetAllUsersQuery({ accountId, ids: changedByUserIds }, { skip: changedByUserIds.length === 0 })

  const userDisplayNameMap = useMemo<Record<string, string>>(() => {
    if (!changedByUsers?.data) return {}
    return Object.fromEntries(changedByUsers.data.map((user: User) => [user.id, getUserDisplayName(user)]))
  }, [changedByUsers])

  const sessionTitleMap = useMeetingTitles(consentChanges?.data)

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const applyConsentChange = tracked(
    async (newValue: UserConsentState, params?: { reason?: string; attachmentFileId?: string }) => {
      const previousValue = selectedConsentState
      setSelectedConsentState(newValue)
      try {
        await updateConsent({
          accountId,
          userId: contact.id,
          newValue,
          type: 'manual',
          reason: params?.reason,
          attachmentFileId: params?.attachmentFileId,
          ...(filterByMeeting ? { roomId, sessionId } : {}),
        }).unwrap()
      } catch {
        setSelectedConsentState(previousValue)
      }
    },
    {
      eventName: TrackEvent.CONSENT_CHANGED,
      eventDetails: {
        user_id: contact.id,
        value: pendingConsentState,
        session_id: sessionId,
        room_id: roomId,
        source: 'consent-history',
      },
    },
    {
      eventDetailsExtractor: (newValue: UserConsentState) => ({ new_value: newValue }),
    },
  )

  const handleConsentChange = (newValue: UserConsentState) => {
    if (requiresConfirmationPanel) {
      setPendingConsentState(newValue)
    } else {
      applyConsentChange(newValue)
    }
  }

  const handleConfirmationSave = async (params: { reason?: string; attachmentFileId?: string }) => {
    if (!pendingConsentState) return
    setIsSaving(true)
    try {
      await applyConsentChange(pendingConsentState, params)
      setPendingConsentState(null)
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmationCancel = () => {
    setPendingConsentState(null)
  }

  const toggleCommentRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const contactDisplayName = getUserDisplayName(contact)

  const columns: TableColumn<ExternalUserConsentChange>[] = [
    {
      name: 'Consent',
      selector: (row) => row.newValue || UserConsentState.UNKNOWN,
      cell: (row) =>
        row.deletedAt ? (
          <span className='border rounded-1 px-2 py-1 paragraph-small zocks-text-gray-600'>Removed</span>
        ) : (
          <SimpleConsentIcon consentState={row.newValue || UserConsentState.UNKNOWN} showText />
        ),
    },
    {
      name: 'Date',
      selector: (row) => row.changedAt,
      cell: (row) =>
        row.type === 'initial' ? (
          <div>-</div>
        ) : (
          <div className='text-nowrap'>
            {new Date(row.changedAt).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
              timeZone: timezone,
            })}
          </div>
        ),
    },
    {
      name: 'Method',
      selector: (row) => consentTypeLabel[row.type] ?? row.type,
    },
    {
      name: 'Meeting',
      selector: (row) => (row.sessionId ? (sessionTitleMap[row.sessionId] ?? row.sessionId) : '-'),
      cell: (row) => {
        if (!row.roomId || !row.sessionId) return <span>-</span>
        const fullTitle = sessionTitleMap[row.sessionId] ?? row.sessionId
        const displayTitle = fullTitle.length > 20 ? `${fullTitle.substring(0, 17)}...` : fullTitle
        return (
          <NavLink
            className={`${buttonStyles.button} ${buttonStyles.primaryLinkButton} text-decoration-underline text-nowrap p-0`}
            to={`/workspace/past-meetings/room/${row.roomId}/session/${row.sessionId}`}
            title={fullTitle}
          >
            {displayTitle}
          </NavLink>
        )
      },
    },
    {
      name: 'Added by',
      selector: (row) =>
        row.changedByUserId && row.changedByUserId !== SERVICE_TOKEN_USER_ID ? (userDisplayNameMap[row.changedByUserId] ?? row.changedByUserId) : '-',
    },
    {
      name: 'Comment',
      cell: (row) =>
        row.deletedAt ? (
          <i
            role='button'
            className={`bi cursor-pointer zocks-text-primary ${expandedRows.has(row.id) ? 'bi-info-circle-fill' : 'bi-info-circle'}`}
            onClick={() => toggleCommentRow(row.id)}
          />
        ) : row.reason ? (
          <i
            role='button'
            className={`bi cursor-pointer zocks-text-primary ${expandedRows.has(row.id) ? 'bi-chat-left-text-fill' : 'bi-chat-left-text'}`}
            onClick={() => toggleCommentRow(row.id)}
          />
        ) : (
          <span>-</span>
        ),
    },
    {
      name: 'Evidence',
      cell: (row) =>
        row.attachmentFileId ? (
          <Button
            variant='primaryLinkButton'
            onClick={async () => {
              const result = await downloadFileRequest({ accountId, fileId: row.attachmentFileId! }).unwrap()
              window.open(result.data.location, '_blank', 'noopener,noreferrer')
            }}
          >
            <i className='bi bi-download' />
          </Button>
        ) : (
          <span>-</span>
        ),
    },
  ]

  const ModalBody = (
    <Container className='p-0'>
      <div className='border rounded-2 p-3 d-flex flex-column gap-3'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-2'>
            <ConsentAvatar name={contactDisplayName} consentState={selectedConsentState} />
            <span className='fw-bold paragraph-medium zocks-text-gray-900'>{contactDisplayName}</span>
          </div>
          {manualConsentEnabled ? (
            <ConsentStateDropdown value={pendingConsentState ?? selectedConsentState} onChange={handleConsentChange} />
          ) : (
            <SimpleConsentIcon consentState={selectedConsentState} showText />
          )}
        </div>
        {pendingConsentState !== null && evidenceUploadConfig && commentUploadConfig ? (
          <ConsentConfirmationPanel
            evidenceUploadConfig={evidenceUploadConfig}
            commentUploadConfig={commentUploadConfig}
            onSave={handleConfirmationSave}
            onCancel={handleConfirmationCancel}
            isLoading={isSaving}
          />
        ) : null}
      </div>
      <div>
        {meetingLevelConsent ? (
          <div className='d-flex align-items-center justify-content-center gap-2 pt-1'>
            <i className='bi bi-info-circle flex-shrink-0 zocks-text-gray-700' />
            <span className='paragraph-small zocks-text-gray-700'>
              {'Based on your '}
              <NavLink
                to='/workspace/setup/settings/compliance'
                className='zocks-text-primary text-decoration-underline'
                target='_blank'
                rel='noopener noreferrer'
              >
                account settings
              </NavLink>
              {', clients must give consent at each meeting, regardless of the status shown above.'}
            </span>
          </div>
        ) : null}
      </div>

      <div className='d-flex flex-column gap-2'>
        <div className='mt-4'>
          <span className='fw-bold paragraph-medium zocks-text-gray-900'>History</span>
        </div>
        <div className='p-0 pb-5'>
          {!isConsentChangesFetching && (!consentChanges?.data || consentChanges.data.length === 0) ? (
            <div className='d-flex flex-column align-items-center justify-content-center text-center px-3 py-5'>
              <span className='fw-bold paragraph-medium zocks-text-gray-900'>No consent history recorded</span>
              <span className='paragraph-medium zocks-text-gray-700'>{"Changes to this client's consent status will appear here."}</span>
            </div>
          ) : (
            <ZocksDataTable
              columns={columns}
              data={consentChanges?.data || []}
              progressPending={isConsentChangesFetching}
              pagination
              paginationServer
              paginationTotalRows={consentChanges?.page.totalElements}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              expandableRows
              expandableRowsHideExpander
              expandableRowExpanded={(row) => expandedRows.has(row.id)}
              expandableRowsComponent={(props) => <CommentExpandableRow {...props} auditLogEnabled={auditLogEnabled} />}
              expandableRowDisabled={(row) => !row.reason && !row.deletedAt && row.type !== 'initial'}
            />
          )}
        </div>
      </div>
    </Container>
  )

  return <Modal className='consent-modal' size={'xl'} show={show} onHide={onCancel} header={<></>} body={ModalBody} centered={true} />
}
